import "./App.css";
import "./i18n"; // Import i18n configuration
import { Routes, Route, Navigate } from "react-router-dom";
import Dashboard from "./pages/dashboard/Dashboard";
import Login from "./pages/login/Login.jsx";
import ProtectedRoute from "./layout/protectedRoute.jsx";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState } from "react";
import Employees from "./pages/employees/Employees.jsx";
import AddEmployee from "./pages/employees/addEmployee/AddEmployee.jsx";
import ViewEmployee from "./pages/employees/ViewEmployee.jsx";
import RecentEntries from "./pages/recentEntries/RecentEntries.jsx";
import PayrollSheet from "./pages/payrollsheet/PayrollSheet.jsx";
import EnhancedPayrollManagement from "./pages/payrollsheet/EnhancedPayrollManagement.jsx";
import Reports from "./pages/reports/Reports.jsx";
import AttendanceReport from "./pages/reports/AttendanceReport.jsx";
import ProductivityReports from "./pages/reports/ProductivityReports.jsx";
import PerformanceReports from "./pages/reports/PerformanceReports.jsx";
import OvertimeReports from "./pages/reports/OvertimeReports.jsx";
import PayrollDeductions from "./pages/payroll/PayrollDeductions.jsx";
import WalletManagement from "./pages/payroll/WalletManagement.jsx";
import EmployeeBankDetails from "./pages/payroll/EmployeeBankDetails.jsx";
import BatchManagement from "./pages/payroll/BatchManagement.jsx";
import HRApprovals from "./pages/payroll/HRApprovals.jsx";
import AccountantApprovals from "./pages/payroll/AccountantApprovals.jsx";
import PayrollSettings from "./pages/payroll/PayrollSettings.jsx";
import SettlementManagement from "./pages/payroll/SettlementManagement.jsx";
import PayrollAllowances from "./pages/payroll/PayrollAllowances.jsx";
import PaymentSuccess from "./pages/payrollsheet/tabs/PaymentSuccess.jsx";
import Settings from "./pages/settings/Settings.jsx";
import Configuration from "./pages/configuration/Configuration.jsx"
import Leave from "./pages/leave/Leave.jsx";
import { SessionWarningProvider } from "./context/SessionWarningContext";
import ResetPassword from "./pages/login/ResetPassword.jsx";
import ErrorBoundary from "./components/ErrorBoundary.jsx";
import { UserProvider } from "./context/UserContext";
import { PlanRestrictionProvider, usePlanRestriction } from "./context/PlanRestrictionContext";
import UpgradeModal from "./components/plan/UpgradeModal.jsx";
import BranchManagement from "./pages/admin/BranchManagement.jsx";
import UserManagement from "./pages/admin/UserManagement.jsx";
import OvertimeManagement from "./pages/admin/OvertimeManagement.jsx";
import VmsLogin from "./vms/pages/VmsLogin.jsx";
import VmsSetupAdmin from "./vms/pages/VmsSetupAdmin.jsx";
import VmsProtectedRoute from "./vms/layout/VmsProtectedRoute.jsx";
import VmsRoleRoute from "./vms/layout/VmsRoleRoute.jsx";
import VmsShell from "./vms/layout/VmsShell.jsx";
import VmsOffices from "./vms/pages/VmsOffices.jsx";
import VmsGates from "./vms/pages/VmsGates.jsx";
import VmsPeopleUsers from "./vms/pages/VmsPeopleUsers.jsx";
import VmsRequests from "./vms/pages/VmsRequests.jsx";
import VmsReportsVisits from "./vms/pages/VmsReportsVisits.jsx";
import VmsReportsSummary from "./vms/pages/VmsReportsSummary.jsx";
import VmsDashboard from "./vms/pages/VmsDashboard.jsx";
import VmsForgotPassword from "./vms/pages/VmsForgotPassword.jsx";
import VmsResetPassword from "./vms/pages/VmsResetPassword.jsx";
import VmsChangePassword from "./vms/pages/VmsChangePassword.jsx";

// Upgrade Modal Wrapper Component
const UpgradeModalWrapper = () => {
  const { upgradeModal, hideUpgradeModal, handleUpgrade, restrictions } = usePlanRestriction();
  
  return (
    <UpgradeModal
      isOpen={upgradeModal.isOpen}
      onClose={hideUpgradeModal}
      restrictionType={upgradeModal.restrictionType}
      restrictionMessage={upgradeModal.restrictionMessage}
      currentPlan={restrictions.planName}
      onUpgrade={handleUpgrade}
    />
  );
};

function App() {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000,
          },
        },
      })
  );

  // fgg

  return (
    <QueryClientProvider client={queryClient}>
      <UserProvider>
        <PlanRestrictionProvider>
          <SessionWarningProvider>
            <div className="App" style={{ overflowX: 'hidden', maxWidth: '100vw' }}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/vms/login" element={<VmsLogin />} />
              <Route path="/vms/forgot-password" element={<VmsForgotPassword />} />
              <Route path="/vms/reset-password" element={<VmsResetPassword />} />
              <Route path="/vms/setup/admin" element={<VmsSetupAdmin />} />
              <Route element={<VmsProtectedRoute />}>
                <Route path="/vms/change-password" element={<VmsChangePassword />} />
                <Route element={<VmsShell />}>
                  <Route path="/vms" element={<Navigate to="/vms/dashboard" replace />} />
                  <Route path="/vms/dashboard" element={<VmsDashboard />} />
                  <Route path="/vms/requests" element={<VmsRequests />} />
                  <Route path="/vms/reports/visits" element={<VmsReportsVisits />} />
                  <Route path="/vms/reports/summary" element={<VmsReportsSummary />} />
                  <Route element={<VmsRoleRoute allowedRoles={["general_admin"]} />}>
                    <Route path="/vms/offices" element={<VmsOffices />} />
                    <Route path="/vms/gates" element={<VmsGates />} />
                    <Route path="/vms/people-users" element={<VmsPeopleUsers />} />
                    <Route path="/vms/persons" element={<Navigate to="/vms/people-users" replace />} />
                    <Route path="/vms/users" element={<Navigate to="/vms/people-users" replace />} />
                  </Route>
                </Route>
              </Route>
              <Route path="/reset-password" element={
                <ErrorBoundary>
                  <ResetPassword />
                </ErrorBoundary>
              } />
              <Route element={<ProtectedRoute />}>
                <Route path="/" element={<Dashboard />} />
                <Route path="/payment-success" element={
                  <ErrorBoundary>
                    <PaymentSuccess />
                  </ErrorBoundary>
                } />
                <Route path="/employee" element={<Employees />} />
                <Route path="/employee/add-employee" element={<AddEmployee />} />
                <Route path="/employee/view-employee/:employeeId" element={<ViewEmployee />} />
                <Route path="/entries" element={<RecentEntries />} />
                <Route path="/payroll" element={<PayrollSheet/>}/>
                <Route path="/payroll/enhanced" element={<EnhancedPayrollManagement/>}/>
                <Route path="/report" element={<Reports/>}/>
                <Route path="/report/attendance" element={<AttendanceReport/>}/>
                <Route path="/report/productivity" element={<ProductivityReports/>}/>
                <Route path="/report/performance" element={<PerformanceReports/>}/>
                <Route path="/report/overtime" element={<OvertimeReports/>}/>
                <Route path="/payroll/deductions" element={<PayrollDeductions/>}/>
                <Route path="/payroll/wallet" element={<WalletManagement/>}/>
                <Route path="/payroll/bank-details" element={<EmployeeBankDetails/>}/>
                <Route path="/payroll/batch" element={<BatchManagement/>}/>
                <Route path="/payroll/hr-approvals" element={<HRApprovals/>}/>
                <Route path="/payroll/accountant-approvals" element={<AccountantApprovals/>}/>
                <Route path="/payroll/settings" element={<PayrollSettings/>}/>
                <Route path="/payroll/settlement" element={<SettlementManagement/>}/>
                <Route path="/payroll/allowances" element={<PayrollAllowances/>}/>
                <Route path="/settings" element={<Settings/>}/>
                <Route path="/configuration" element={<Configuration/>}/>
                <Route path="/leave" element={<Leave/>}/>
                {/* Admin Routes */}
                <Route path="/admin/branches" element={<BranchManagement />} />
                <Route path="/admin/users" element={<UserManagement />} />
                <Route path="/admin/overtime" element={<OvertimeManagement />} />
              </Route>
            </Routes>
            
            {/* Global Upgrade Modal */}
            <UpgradeModalWrapper />
          </div>
          </SessionWarningProvider>
        </PlanRestrictionProvider>
      </UserProvider>
    </QueryClientProvider>
  );
}

export default App;












